import { useState, useEffect } from "react";
import React from "react";
import DOMPurify from "dompurify"; // Ensure you install this package
import Sidenav from "../components/Sidenav";
import Toolbar from "@mui/material/Toolbar";
import { experimentalStyled as styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import Card from '@mui/material/Card';
import Paper from "@mui/material/Paper";
import axios from "axios";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TextField from "@mui/material/TextField";
import { Stack } from "@mui/material";
import { TextareaAutosize as BaseTextareaAutosize } from "@mui/base/TextareaAutosize";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Swal from "sweetalert2";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers/DateField";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import { set } from "date-fns";
import config from "../config/config";

// For rich text
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const drawerWidth = 240;
const token = localStorage.getItem("token")?.replace(/['"]/g, "");

const blue = {
  100: "#DAECFF",
  200: "#b6daff",
  400: "#3399FF",
  500: "#007FFF",
  600: "#0072E5",
  900: "#003A75",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Textarea = styled(BaseTextareaAutosize)(
  ({ theme }) => `
  box-sizing: border-box;
  width: 320px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 8px;
  color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
  background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
  border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
  box-shadow: 0 2px 2px ${theme.palette.mode === "dark" ? grey[900] : grey[50]};

  &:hover {
    border-color: ${blue[400]};
  }

  &:focus {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${theme.palette.mode === "dark" ? blue[600] : blue[200]};
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }
`
);

const paginationModel = { page: 0, pageSize: 5 };

export default function VisitDetails() {
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <div>
          {/* Edit Button */}
          <IconButton
            color="primary"
            onClick={() => handleEdit(params.row)}
            title="Edit"
            width={100}
          >
            <EditIcon />
          </IconButton>
          {/* Delete Button */}
          <IconButton
            color="secondary"
            onClick={() => handleDelete(params.row)}
            title="Delete"
            width={100}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
    {
      field: "date_of_visit",
      headerName: "Visit Date",
      description: "This column has a value getter and is not sortable.",

      width: 160,
    },
    { field: "proj_name", headerName: "Project Name", width: 150 },
    { field: "purpose", headerName: "Purpose", width: 150 },

    {
      field: "remarks",
      headerName: "Notes",
      width: 350,
      renderCell: (params) => (
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(params.value || ""),
          }}
          style={{
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.4",
            height: "auto", // Limit height to prevent overflow
            wordBreak: "break-word",
          }}
        />
      ),
    },
  ];
  useEffect(() => {
    fetchData(); // Call the fetchData function when the page loads
  }, []);

  const [rows, setRows] = useState([]); // State to hold API data
  const [prodRows, setProdRows] = useState([]);
  const [purposeRows, setPurposeRows] = useState([]);
  const [loading, setLoading] = useState(true); // State for loading indicator
  const [open, setOpen] = React.useState(false);
  // const handleOpen = () => setOpen(true);
  // const handleClose = () => setOpen(false);
  const [prod_name, setProdName] = useState("");
  const [prod_nameError, setProdNameError] = useState("");
  const [rate, setRate] = useState("");
  const [rateError, setRateError] = useState("");
  const [date_of_purchase, setDateOfPurchase] = useState("");
  const [date_of_purchase_error, setDateOfPurchase_error] = useState("");
  const [selectedDate, setSelectedDate] = useState("dd-MM-yyyy");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null); // State to hold uploaded image URL
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [selectedPurpose, setSelectedPurpose] = React.useState(null);
  const [selectedProductId, setSelectedProductId] = React.useState(null);
  const [selectedPurposeId, setSelectedPurposeId] = React.useState(null);
  const [remarks, setRemarks] = useState("");
  const [qty, setQty] = useState(0);
  const [qtyError, setQtyError] = useState("");
  const maxChars = 1000;

  // const handleNotesChange = (e) => {
  //   const input = e.target.value;
  //   if (input.length <= maxChars) {
  //     setRemarks(input);
  //   }
  // };
  const handleNotesChange = (value) => {
    if (value.replace(/<[^>]+>/g, "").length <= maxChars) {
      setRemarks(value);
    }
  };
  const handleOpen = (param) => {
    if (param === "add") {
      setSelectedProductId("");
      setSelectedProduct("");
      setSelectedPurposeId("");
      setSelectedPurpose("");
      setDateOfPurchase("");
      setRemarks("");
      setSelectedUserId("");
      setSelectedDate("");
    }
    setOpen(true);
  };
  const handleClose = (e) => {
    setSelectedProductId("");
    setSelectedProduct("");
    setSelectedPurposeId("");
    setSelectedPurpose("");
    setDateOfPurchase("");
    setRemarks("");
    setSelectedUserId("");
    setSelectedDate("");
    setOpen(false);
  };

  const handleEdit = async (row) => {
    try {
      const response = await axios.get(
        `${config.baseUrl}/Details/detailsById?id=${row.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const userData = response.data[0]; // Access the first object in the array

      if (userData) {
        // Log the fetched user data for debugging
        console.log("Fetched details data:", userData);

        // Set state
        setSelectedProductId(userData.proj_id || "");
        setSelectedProduct(userData.proj_name || "");
        setSelectedPurposeId(userData.purpose_id || "");
        setSite(userData.site || "");
        setSelectedPurpose(userData.purpose || "");
        setDateOfPurchase(userData.date_of_visit || "");
        console.log(selectedProduct);
        console.log(selectedPurpose);
        setRemarks(userData.remarks || "");
        setSelectedUserId(row.id);

        // Open modal after setting data
        handleOpen("edit");
      } else {
        console.error("Product data not found in response");
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };

  const handleDelete = async (row) => {
    try {
      // Fetch user details
      const response = await axios.get(
        `${config.baseUrl}/Details/detailsById?id=${row.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const userData = response.data[0]; // Access the first object in the array

      if (userData) {
        // Log the fetched user data for debugging
        console.log("Fetched product data:", userData);

        // Show confirmation alert
        const result = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
          try {
            // Send delete request
            const deleteResponse = await axios.post(
              `${config.baseUrl}/Details/deleteDetails`,
              { id: row.id },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            // Handle delete response
            if (deleteResponse.status === 200) {
              console.log(
                "Details successfully deleted:",
                deleteResponse.data.executeMessage
              );

              // Refresh the data
              Swal.fire("Deleted!", "The product has been deleted.", "success");
              fetchPurchaseData();
              window.location.reload();
            } else {
              console.error(
                "Error deleting user:",
                deleteResponse.data.executeMessage
              );
              Swal.fire("Error", "Failed to delete the product.", "error");
            }
          } catch (deleteError) {
            console.error("Error during deletion:", deleteError);
            Swal.fire("Error", "An error occurred during deletion.", "error");
          }
        } else {
          console.log("Product deletion cancelled");
        }
      } else {
        console.error("Product data not found in response");
        Swal.fire("Error", "Product not found.", "error");
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      Swal.fire(
        "Error",
        "An error occurred while fetching product details.",
        "error"
      );
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem("token")?.replace(/['"]/g, "");
    setLoading(true); // Turn on loading state before the API call
    try {
      const response = await axios.get(
        `${config.baseUrl}/Projects/projects`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      // Map API response to match DataGrid row structure
      const mappedRows = response.data.map((item, index) => ({
        id: item.id,
        //img: item.img, // Ensure your API returns `imgUrl`
        proj_name: item.proj_name,
        //start_dt: item.start_dt,
        //rate: item.rate,
      }));
      setProdRows(mappedRows); // Update rows state with mapped data
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false); // Turn off loading state after the API call is finished
    }
  };

  const fetchPurposeData = async () => {
    const token = localStorage.getItem("token")?.replace(/['"]/g, "");
    setLoading(true); // Turn on loading state before the API call
    try {
      const response = await axios.get(
        `${config.baseUrl}/Purpose/purpose`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      // Map API response to match DataGrid row structure
      const mappedRows = response.data.map((item, index) => ({
        id: item.id,
        //img: item.img, // Ensure your API returns `imgUrl`
        purpose: item.purpose,
        //start_dt: item.start_dt,
        //rate: item.rate,
      }));
      setPurposeRows(mappedRows); // Update rows state with mapped data
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false); // Turn off loading state after the API call is finished
    }
  };

  const fetchPurchaseData = async () => {
    const token = localStorage.getItem("token")?.replace(/['"]/g, "");
    setLoading(true); // Turn on loading state before the API call
    try {
      const response = await axios.get(
        `${config.baseUrl}/Details/details`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      // Map API response to match DataGrid row structure
      const mappedRowspurchase = response.data.map((item, index) => ({
        id: item.id,
        proj_id: item.proj_id,
        proj_name: item.proj_name,
        purpose_id: item.purpose_id,
        purpose: item.purpose,
        date_of_visit: item.date_of_visit,
        remarks: item.remarks,
        created_by: item.created_by,
      }));

      setRows(mappedRowspurchase);
      console.log(mappedRowspurchase); // Update rows state with mapped data
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false); // Turn off loading state after the API call is finished
    }
  };
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: "10px",
    height: 450,
    overflow: "auto",
  };

  const [site, setSite] = React.useState("");

  const handleChange = (event) => {
    setSite(event.target.value);
  };
  const handleDateChange = (newValue) => {
    setSelectedDate(newValue.format("YYYY-MM-DD")); // Update the local state
    handleDateOfPurchaseChange(newValue); // Pass the newValue to the parent handler
  };

  const handleDateOfPurchaseChange = (newValue) => {
    if (newValue === null) {
      setDateOfPurchase_error("Date must not be empty.");
    } else {
      setDateOfPurchase_error(""); // Clear error if valid
    }
    setDateOfPurchase(newValue); // Set the selected date in state
    //setDateOfPurchase(newValue.format("DD-MM-YYYY"));
  };

  const handleSelectedProductChange = (event, newValue) => {
    if (newValue) {
      setSelectedProduct(newValue.proj_name || ""); // Set the selected product name
      setSelectedProductId(newValue.id || ""); // Set the selected product ID
    } else {
      setSelectedProduct(""); // Clear the product name if no value is selected
      setSelectedProductId(""); // Clear the product ID if no value is selected
    }
  };

  const handleSelectedPurposeChange = (event, newValue) => {
    if (newValue) {
      setSelectedPurpose(newValue.purpose || ""); // Set the selected product
      //setRate(newValue ? newValue.rate : ""); // Update the rate state
      setSelectedPurposeId(newValue ? newValue.id : "");
    } else {
      setSelectedPurposeId("");
      setSelectedPurpose("");
    }
  };

  const handleQtyChange = (e) => {
    const value = e.target.value;
    if (value <= 0) {
      setQtyError("Quantity must not be empty.");
    } else {
      setQtyError("");
    }
    setQty(value);
  };

  useEffect(() => {
    fetchData();
    fetchPurposeData();
    fetchPurchaseData();
    setSelectedDate(dayjs(date_of_purchase || new Date()));
  }, [date_of_purchase]);

  const handleSubmit = async () => {
    // Create the data object that you want to send in the API request
    const userData = {
      proj_id: selectedProductId,
      purpose_id: selectedPurposeId,
      site: site,
      date_of_visit: selectedDate.format("YYYY-MM-DD"),
      remarks: remarks,
      creator_id: localStorage.getItem("user_id"),
    };

    try {
      // Log the data to the console for debugging
      console.log("Form Data Submitted:");
      console.log(userData);

      // Call the API with a POST request
      const response = await axios.post(
        `${config.baseUrl}/Details/addDetails`,
        userData, // Send the data as the request body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json", // Set the content type to JSON
          },
        }
      );

      // Handle the response
      if (response.status === 200) {
        console.log("Product request sent to admin");

        // Clear form fields
        setSelectedProduct("");
        setSelectedProductId("");
        setRate("");
        setQty(0); // Clear the uploaded image URL

        setOpen(false); // Close the modal
        // Show success message using SweetAlert
        Swal.fire({
          title: "Success!",
          text: response.data.executeMessage,
          icon: "success",
          confirmButtonText: "OK",
        });

        // Optionally, call fetchData to refresh the data after adding a new user
        fetchPurchaseData();
      }
    } catch (error) {
      // Handle any errors
      console.error("Error sending product request:", error);
      setOpen(false);
      // Show error message using SweetAlert
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while processing your request. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleUpdate = async () => {
    // Create the data object that you want to send in the API request
    const userData = {
      id: selectedUserId,
      proj_id: selectedProductId,
      purpose_id: selectedPurposeId,
      site: site,
      date_of_visit: selectedDate.format("YYYY-MM-DD"),
      remarks: remarks,
      modifier_id: localStorage.getItem("user_id"),
    };

    try {
      // Log the data to the console for debugging
      console.log("Form Data Submitted:");
      console.log(userData);

      // Call the API with a POST request
      const response = await axios.post(
        `${config.baseUrl}/Details/editDetails`,
        userData, // Send the data as the request body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json", // Set the content type to JSON
          },
        }
      );

      // Handle the response
      if (response.status === 200) {
        console.log(
          "Product successfully added:",
          response.data.executeMessage
        );

        // Clear form fields
        setProdName("");
        setDateOfPurchase("");
        setRate("");
        setUploadedImage(null); // Clear the uploaded image URL
        setOpen(false); // Close the modal
        // Show success message using SweetAlert
        Swal.fire({
          title: "Success!",
          text: response.data.executeMessage,
          icon: "success",
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.reload();
          }
        });

        // Optionally, call fetchData to refresh the data after adding a new user

        fetchData();
      }
    } catch (error) {
      // Handle any errors
      console.error("Error adding product:", error);
      setOpen(false);
      // Show error message using SweetAlert
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while editing the product. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div>
      <Box />

      <Box sx={{ display: "flex" }}>
        <Sidenav />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <Toolbar />
          <Button
            variant="contained"
            onClick={() => handleOpen("add")}
            startIcon={<AddCircleOutlineIcon />}
            sx={{ mb: 2 }}
          >
            Add Details
          </Button>
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style}>
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                textAlign="center"
                fontWeight={"bold"}
              >
                {selectedUserId == null || selectedUserId === ""
                  ? "Add Details"
                  : "Edit Details"}
                <hr />
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                <Autocomplete
                  id="product-select"
                  sx={{ width: "100%", mb: 2 }}
                  value={
                    prodRows.find(
                      (product) => product.id === selectedProductId
                    ) || null
                  } // Find the selected product by id
                  options={prodRows} // Available products in the dropdown
                  getOptionLabel={(option) => option.proj_name || ""} // Display the project name
                  loading={loading}
                  size="small"
                  onChange={handleSelectedProductChange}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "& > img": { mr: 2 },
                      }}
                      {...props}
                    >
                      <div>{option.proj_name}</div> {/* Display project name */}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search for a project"
                      InputProps={{
                        ...params.InputProps,
                        autoComplete: "new-password", // Disable autocomplete
                      }}
                    />
                  )}
                />

                <Autocomplete
                  id="purpose-select"
                  sx={{ width: "100%", mb: 2 }}
                  value={
                    purposeRows.find(
                      (product) => product.id === selectedPurposeId
                    ) || null
                  } // Find the selected product by id
                  options={purposeRows} // Available products in the dropdown
                  getOptionLabel={(option) => option.purpose || ""} // Display the project name
                  loading={loading}
                  size="small"
                  onChange={handleSelectedPurposeChange}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "& > img": { mr: 2 },
                      }}
                      {...props}
                    >
                      <div>{option.purpose}</div> {/* Display project name */}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search for a purpose"
                      InputProps={{
                        ...params.InputProps,
                        autoComplete: "new-password", // Disable autocomplete
                      }}
                    />
                  )}
                />

                <FormControl fullWidth size="small">
                  <InputLabel id="demo-simple-select-label">Site</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={site}
                    label="Site"
                    onChange={handleChange}
                  >
                    <MenuItem value={"Onsite"}>Onsite</MenuItem>
                    <MenuItem value={"Offsite"}>Offsite</MenuItem>
                  </Select>
                </FormControl>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ width: "100%", mb: 2, mt: 2 }}
                    label="Visit Date"
                    value={
                      date_of_purchase
                        ? dayjs(date_of_purchase, "YYYY-MM-DD")
                        : null
                    }
                    onChange={handleDateChange}
                    error={!!date_of_purchase_error}
                    helperText={date_of_purchase_error}
                    format="YYYY-MM-DD"
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                  />
                </LocalizationProvider>

                {/* <Stack spacing={2} direction="row" sx={{ width: "100%" }}>
                  <Textarea
                    maxRows={4}
                    aria-label="maximum height"
                    placeholder="Notes"
                    style={{ width: "100%", height: "50px" }}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </Stack> */}

                {/* <Paper
                  elevation={0}
                  sx={{
                    width: "100%",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <TextField
                    multiline
                    placeholder="Notes"
                    value={remarks}
                    onChange={handleNotesChange}
                    inputProps={{
                      maxLength: maxChars,
                    }}
                    variant="standard"
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": {
                        height: "70px",
                        padding: "8px 12px",
                      },
                      "& .MuiInputBase-input": {
                        maxHeight: "70px !important",
                        overflowY: "auto !important",
                        "&::-webkit-scrollbar": {
                          width: "8px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "rgba(0,0,0,0.2)",
                          borderRadius: "4px",
                        },
                      },
                      "& .MuiInput-underline:before": {
                        display: "none",
                      },
                      "& .MuiInput-underline:after": {
                        display: "none",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "divider",
                      padding: "4px 12px",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      bgcolor: "grey.50",
                      borderBottomLeftRadius: "inherit",
                      borderBottomRightRadius: "inherit",
                    }}
                  >
                    {remarks.length}/{maxChars} characters
                  </Box>
                </Paper> */}

                <Paper
                  elevation={2}
                  sx={{
                    padding: "10px",
                    borderRadius: "8px",
                    bgcolor: "white",
                    height: "200px",
                    position: "relative", // Allows absolute positioning inside
                  }}
                >
                  <ReactQuill
                    value={remarks}
                    onChange={handleNotesChange}
                    theme="snow"
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ align: [] }],
                        ["link"],
                        ["clean"],
                      ],
                    }}
                    formats={[
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "list",
                      "bullet",
                      "align",
                      "link",
                    ]}
                    style={{
                      height: "100px", // Reduced height to avoid overlap
                      borderRadius: "6px",
                      margin: "0 auto",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "10px",
                      right: "10px",
                      fontSize: "0.8rem",
                      color: "gray",
                      backgroundColor: "white",
                      padding: "3px 6px",
                      borderRadius: "4px",
                      boxShadow: "0px 1px 3px rgba(0,0,0,0.1)", // Light shadow for visibility
                    }}
                  >
                    {remarks.replace(/<[^>]+>/g, "").length} / 1000
                  </Box>
                </Paper>

                <Stack
                  spacing={2}
                  direction="row"
                  sx={{ mt: 2 }}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: "#347928" }}
                    onClick={
                      selectedUserId == null || selectedUserId === ""
                        ? handleSubmit
                        : handleUpdate
                    }
                  >
                    {selectedUserId == null || selectedUserId === ""
                      ? "Submit"
                      : "Update"}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: "#D7D3BF", color: "#000" }}
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </Stack>
              </Typography>
            </Box>
          </Modal>
          <Paper sx={{ height: 400, width: "100%"}}>
          <Card className="w-full">
      <DataGrid
        
        rows={rows}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 5, page: 0 }
          }
        }}
        pageSizeOptions={[5, 10]}
        className="border-0"
        // Enable auto-height for rows
        getRowHeight={() => 'auto'}
        // Add minimum row height
        sx={
          {
          
          '& .MuiDataGrid-row': {
            minHeight: '52px !important'
          },
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }
        }}
      />
    </Card>
          </Paper>
        </Box>
      </Box>
    </div>
  );
}
